import {
	TFileId,
	ICreateModifierValidationError,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	IServerError,
	TLanguageIDs,
	IMenuTranslation,
	IDefaultLanguage,
	IUpdateModifier,
	ITotalPrice,
} from 'types';
import { IInitialFormData, TEditModifierVisibleForm, TTypeOfEditModifierModalWindow } from './editModifierContext';

export enum EditModifierActionTypes {
	REFETCH_DATA = 'REFETCH_DATA',
	SET_INITIAL_FORM_DATA = 'SET_INITIAL_FORM_DATA',
	SET_ALL_DATA = 'SET_ALL_DATA',
	CHANGE_VISIBLE_FORM = 'CHANGE_VISIBLE_FORM',
	SET_FILE_ID = 'SET_FILE_ID',
	DELETE_PHOTO = 'DELETE_PHOTO',
	SET_CUSTOMER = 'SET_ID_CUSTOMER',
	SET_FETCHED_MODIFIER_GROUPS = 'SET_FETCHED_MODIFIER_GROUPS',
	SET_MODIFIER_GROUPS = 'SET_IDs_MODIFIER_GROUPS',
	CHANGE_NAME = 'CHANGE_NAME',
	CHANGE_EXTERNAL_ID = 'CHANGE_EXTERNAL_ID',
	CHANGE_DESCRIPTION = 'CHANGE_DESCRIPTION',
	SET_FETCHED_RESTAURANTS = 'SET_FETCHED_RESTAURANTS',
	SET_RESTAURANTS_FOR_PRICE = 'SET_RESTAURANTS_FOR_PRICE',
	CHANGE_PRICE = 'CHANGE_PRICE',
	ADD_PRICE = 'ADD_PRICE',
	REMOVE_PRICE = 'REMOVE_PRICE',
	SET_IDS_IS_FORCE_DISABLED_RESTAURANT = 'SET_IDS_IS_FORCE_DISABLED_RESTAURANT',
	SET_IS_DISABLED_IN_RESTAURANTS = 'SET_IS_DISABLED_IN_RESTAURANTS',
	SET_IS_RESTRICTED_IN_RESTAURANTS = 'SET_IS_RESTRICTED_IN_RESTAURANTS',
	SET_IS_PRESELECTED_IN_RESTAURANTS = 'SET_IS_PRESELECTED_IN_RESTAURANTS',
	SET_QUANTITY_REGULATION_RESTAURANTS = 'SET_QUANTITY_REGULATION_RESTAURANTS',
	CHANGE_QUANTITY_MAX_LIMIT = 'CHANGE_QUANTITY_MAX_LIMIT',
	SET_DEFAULT_CUSTOMER_LANGUAGE = 'SET_DEFAULT_CUSTOMER_LANGUAGE',
	SET_TRANSLATION_TAB = 'SET_TRANSLATION_TAB',
	CHANGE_TRANSLATION = 'CHANGE_TRANSLATION',
	CHANGE_ACTIVATION_SCHEDULE = 'CHANGE_ACTIVATION_SCHEDULE',
	CHANGE_DEACTIVATION_SCHEDULE = 'CHANGE_DEACTIVATION_SCHEDULE',
	SET_DATA_FOR_SUBMIT = 'SET_DATA_FOR_SUBMIT',
	CHANGE_TYPE_OF_MODAL_WINDOW = 'CHANGE_TYPE_OF_MODAL_WINDOW',
	SHOW_ERROR_MODAL_WITH_INFO = 'SHOW_ERROR_MODAL_WITH_INFO',
	RESET_FIELDS = 'RESET_FIELDS',
}

interface IRefetchData {
	type: EditModifierActionTypes.REFETCH_DATA;
	payload: {
		needRefetch: boolean;
	};
}

interface ISetInitialFormData {
	type: EditModifierActionTypes.SET_INITIAL_FORM_DATA;
	payload: {
		initialFormData: IInitialFormData;
	};
}

interface ISetAllData {
	type: EditModifierActionTypes.SET_ALL_DATA;
	payload: {
		modifierId: string;
		filePath: Nullable<string>;
		preSignedUrl: string | null;
		selectedCustomer: Nullable<string>;
		restaurantsForPrice: string[];
		totalPrices: ITotalPrice[];
		IDsIsForceDisabledRestaurant: string[];
		selectedModifierGroups: string[];
		name: string;
		articleNumber: Nullable<string>;
		description: string;
		externalId: string;
		isDisabledInRestaurants: string[];
		isRestrictedInRestaurants: string[];
		isPreselectedInRestaurants: string[];
		quantityRegulationRestaurants: string[];
		quantityMaxLimit: number | null;
		activationSchedule: string | null;
		deactivationSchedule: string | null;
		translations: IMenuTranslation[];
	};
}

interface IChangeVisibleForm {
	type: EditModifierActionTypes.CHANGE_VISIBLE_FORM;
	payload: {
		visibleForm: TEditModifierVisibleForm;
	};
}

interface ISetFileId {
	type: EditModifierActionTypes.SET_FILE_ID;
	payload: {
		fileId: TFileId;
	};
}

interface IDeletePhoto {
	type: EditModifierActionTypes.DELETE_PHOTO;
}

interface ISetCustomer {
	type: EditModifierActionTypes.SET_CUSTOMER;
	payload: {
		customer: Nullable<string>;
	};
}

interface ISetFetchedRestaurants {
	type: EditModifierActionTypes.SET_FETCHED_RESTAURANTS;
	payload: {
		restaurants: IItemForGetShortInfoWithStatus[];
	};
}

interface ISetRestaurantsForPrice {
	type: EditModifierActionTypes.SET_RESTAURANTS_FOR_PRICE;
	payload: {
		restaurants: string[];
	};
}

interface IChangePrice {
	type: EditModifierActionTypes.CHANGE_PRICE;
	payload: {
		price: string;
	};
}

interface IAddPrice {
	type: EditModifierActionTypes.ADD_PRICE;
	payload: {
		prices: ITotalPrice[];
	};
}

interface IRemovePrice {
	type: EditModifierActionTypes.REMOVE_PRICE;
	payload: {
		updatedTotalPrices: ITotalPrice[];
		updatedRestaurantsForPrices: string[];
		updatedIsDisabledInRestaurants: string[];
		updatedIsRestrictedInRestaurants: string[];
		updatedIsPreselectedInRestaurants: string[];
		updatedQuantityRegulationRestaurants: string[];
		updatedIDsIsForceDisabledRestaurant: string[];
	};
}

interface ISetIDsIsForceDisabledRestaurant {
	type: EditModifierActionTypes.SET_IDS_IS_FORCE_DISABLED_RESTAURANT;
	payload: {
		IDsIsForceDisabledRestaurant: string[];
	};
}

interface ISetFetchedModifierGroups {
	type: EditModifierActionTypes.SET_FETCHED_MODIFIER_GROUPS;
	payload: {
		modifierGroups: IItemForGetShortInfo[];
	};
}

interface ISetModifierGroups {
	type: EditModifierActionTypes.SET_MODIFIER_GROUPS;
	payload: {
		modifierGroups: string[];
	};
}

interface IChangeName {
	type: EditModifierActionTypes.CHANGE_NAME;
	payload: {
		name: string;
	};
}

interface IChangeExternalId {
	type: EditModifierActionTypes.CHANGE_EXTERNAL_ID;
	payload: {
		externalId: string;
	};
}

interface IChangeDescription {
	type: EditModifierActionTypes.CHANGE_DESCRIPTION;
	payload: {
		description: string;
	};
}

interface ISetIsDisabledInRestaurants {
	type: EditModifierActionTypes.SET_IS_DISABLED_IN_RESTAURANTS;
	payload: {
		disabledInRestaurants: string[];
	};
}

interface ISetIsRestrictedInRestaurants {
	type: EditModifierActionTypes.SET_IS_RESTRICTED_IN_RESTAURANTS;
	payload: {
		restrictedInRestaurants: string[];
	};
}

interface ISetIsPreselectedInRestaurants {
	type: EditModifierActionTypes.SET_IS_PRESELECTED_IN_RESTAURANTS;
	payload: {
		preselectedInRestaurants: string[];
	};
}

interface ISetQuantityRegulationRestaurants {
	type: EditModifierActionTypes.SET_QUANTITY_REGULATION_RESTAURANTS;
	payload: {
		quantityRegulationRestaurants: string[];
	};
}

interface IChangeQuantityMaxLimit {
	type: EditModifierActionTypes.CHANGE_QUANTITY_MAX_LIMIT;
	payload: {
		quantityMaxLimit: number;
	};
}

interface ISetDefaultCustomerLanguage {
	type: EditModifierActionTypes.SET_DEFAULT_CUSTOMER_LANGUAGE;
	payload: {
		defaultCustomerLanguage: Nullable<IDefaultLanguage>;
	};
}

interface ISetTranslationTab {
	type: EditModifierActionTypes.SET_TRANSLATION_TAB;
	payload: {
		selectedTranslationTab: TLanguageIDs | null;
	};
}

interface IChangeTranslation {
	type: EditModifierActionTypes.CHANGE_TRANSLATION;
	payload: {
		translations: IMenuTranslation[];
	};
}

interface IChangeActivationSchedule {
	type: EditModifierActionTypes.CHANGE_ACTIVATION_SCHEDULE;
	payload: {
		activationSchedule: string | null;
	};
}

interface IChangeDeactivationSchedule {
	type: EditModifierActionTypes.CHANGE_DEACTIVATION_SCHEDULE;
	payload: {
		deactivationSchedule: string | null;
	};
}

interface ISetDataForSubmit {
	type: EditModifierActionTypes.SET_DATA_FOR_SUBMIT;
	payload: {
		dataForSubmit: IUpdateModifier | null;
	};
}

interface IChangeTypeOfModalWindow {
	type: EditModifierActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW;
	payload: {
		typeOfModalWindow: TTypeOfEditModifierModalWindow;
	};
}

interface IResetSelectedControls {
	type: EditModifierActionTypes.RESET_FIELDS;
}

interface IShowErrorModalWithInfo {
	type: EditModifierActionTypes.SHOW_ERROR_MODAL_WITH_INFO;
	payload: {
		errorResponseData: IServerError<ICreateModifierValidationError> | null;
	};
}

export type TEditModifierAction =
	| IRefetchData
	| ISetInitialFormData
	| ISetAllData
	| IChangeVisibleForm
	| ISetFileId
	| IDeletePhoto
	| ISetCustomer
	| ISetFetchedRestaurants
	| ISetRestaurantsForPrice
	| IChangePrice
	| IAddPrice
	| IRemovePrice
	| ISetIDsIsForceDisabledRestaurant
	| ISetFetchedModifierGroups
	| ISetModifierGroups
	| IChangeName
	| IChangeExternalId
	| IChangeDescription
	| ISetIsDisabledInRestaurants
	| ISetIsRestrictedInRestaurants
	| ISetIsPreselectedInRestaurants
	| ISetQuantityRegulationRestaurants
	| IChangeQuantityMaxLimit
	| ISetDefaultCustomerLanguage
	| ISetTranslationTab
	| IChangeTranslation
	| IChangeActivationSchedule
	| IChangeDeactivationSchedule
	| ISetDataForSubmit
	| IChangeTypeOfModalWindow
	| IShowErrorModalWithInfo
	| IResetSelectedControls;
